import { SPEAuthProvider } from "./speAuthProvider";
import SharePointEmbeddedService, { SPEContainer, SPEDocument } from "./sharePointEmbeddedService";
import { Matter } from "../types";
import { apiClient } from "./apiClient";

/**
 * Document metadata specific to legal matter management
 */
export interface LegalDocument extends SPEDocument {
  matterId: string;
  category?: 'contract' | 'correspondence' | 'research' | 'evidence' | 'pleading' | 'medical_records' | 'other';
  confidentialityLevel?: 'public' | 'internal' | 'confidential' | 'restricted';
  tags?: string[];
  version?: number;
}

/**
 * Document upload result with legal-specific metadata
 */
export interface DocumentUploadResult {
  document: LegalDocument;
  containerId: string;
  success: boolean;
  error?: string;
}

/**
 * Legal Document Service
 * Provides document management capabilities specific to legal matter management
 * Built on top of SharePoint Embedded services
 */
export class LegalDocumentService {
  private speService: SharePointEmbeddedService;
  private authProvider: SPEAuthProvider;
  
  // Track ongoing container operations to prevent race conditions
  private static containerOperations = new Map<string, Promise<{ containerId: string; updatedMatter?: Matter }>>();

  constructor() {
    this.authProvider = new SPEAuthProvider();
    this.speService = new SharePointEmbeddedService(this.authProvider);
  }

  /**
   * Ensures a matter has a SharePoint Embedded container for document storage
   * Uses backend API with application permissions for container creation, preventing permission issues
   * @param matter Matter entity
   * @returns Container ID for the matter and updated matter entity
   */
  public async ensureMatterContainer(matter: Matter): Promise<{ containerId: string; updatedMatter?: Matter }> {
    const operationKey = `matter-${matter.id}`;
    
    // Check if there's already an ongoing operation for this matter
    if (LegalDocumentService.containerOperations.has(operationKey)) {
      console.info(`🔄 Reusing ongoing container operation for matter ${matter.id}`);
      return await LegalDocumentService.containerOperations.get(operationKey)!;
    }
    
    // Create and store the operation promise
    const operation = this.performBackendContainerOperation(matter);
    LegalDocumentService.containerOperations.set(operationKey, operation);
    
    try {
      const result = await operation;
      return result;
    } finally {
      // Clean up the operation tracking
      LegalDocumentService.containerOperations.delete(operationKey);
    }
  }

  /**
   * Backend-powered container operation using application permissions
   * Uses the backend API that has the required SharePoint environment variables configured
   */
  private async performBackendContainerOperation(matter: Matter): Promise<{ containerId: string; updatedMatter?: Matter }> {
    const timestamp = new Date().toISOString();
    console.info(`🚀 [${timestamp}] Starting backend container operation for matter ${matter.id}`);
    
    try {
      // Call our backend API that uses application permissions
      const result = await apiClient.ensureMatterContainer(matter.id, matter.title, matter.clientId);
      
      if (result.success && result.data) {
        console.info(`✅ [${timestamp}] Backend successfully ${result.data.isNew ? 'created' : 'found'} container ${result.data.containerId} for matter ${matter.id}`);
        
        return { 
          containerId: result.data.containerId,
          // Backend handles database updates
        };
      } else {
        throw new Error(result.error || result.message || 'Unknown error from backend container API');
      }
    } catch (error) {
      console.error(`💥 [${timestamp}] Backend container operation failed for matter ${matter.id}:`, error);
      
      // Enhanced error handling for intermittent issues
      if (error instanceof Error) {
        // Handle specific API error conditions
        if (error.message.includes('SERVER_ERROR') || error.message.includes('Internal server error')) {
          throw new Error(`The document service is temporarily unavailable. This usually resolves automatically - please try again in a moment.`);
        }
        if (error.message.includes('NETWORK_ERROR')) {
          throw new Error(`Unable to connect to document service. Please check your connection and try again.`);
        }
        if (error.message.includes('SharePoint Embedded not configured')) {
          throw new Error(`SETUP_REQUIRED: SharePoint Embedded environment variables not configured in backend. Please contact your administrator to set up document management.`);
        }
        if (error.message.includes('403') || error.message.includes('Forbidden')) {
          throw new Error(`SETUP_REQUIRED: SharePoint Embedded permissions not configured. Please contact your administrator to set up document management.`);
        }
        if (error.message.includes('404') || error.message.includes('Not Found')) {
          throw new Error(`SETUP_REQUIRED: SharePoint Embedded container type not found. Please contact your administrator to complete the setup.`);
        }
      }
      
      throw new Error(`Container management failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Uploads a document to a matter's container
   * @param file File to upload
   * @param matter Matter to associate document with
   * @param category Document category (optional)
   * @param confidentialityLevel Confidentiality level (optional)
   * @param tags Document tags (optional)
   */
  public async uploadDocument(
    file: File,
    matter: Matter,
    category: LegalDocument['category'] = 'other',
    confidentialityLevel: LegalDocument['confidentialityLevel'] = 'internal',
    tags: string[] = [],
    folderPath?: string
  ): Promise<DocumentUploadResult> {
    try {
      // Ensure matter has a container
      const { containerId } = await this.ensureMatterContainer(matter);

      // Validate file type and size
      this.validateFile(file);

      // Upload file to SharePoint Embedded
      const uploadedDoc = await this.speService.uploadFile(file, containerId, folderPath || "");

      // Create legal document metadata
      const legalDocument: LegalDocument = {
        ...uploadedDoc,
        matterId: matter.id,
        category,
        confidentialityLevel,
        tags,
        version: 1
      };

      return {
        document: legalDocument,
        containerId,
        success: true
      };
    } catch (error) {
      console.error("Error uploading document:", error);
      return {
        document: {} as LegalDocument,
        containerId: '',
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Gets all documents for a specific matter
   * @param matter Matter to get documents for
   * @returns Array of legal documents
   */
  public async getMatterDocuments(matter: Matter): Promise<{ documents: LegalDocument[]; containerCreated?: boolean }> {
    try {
      // Ensure matter has a container
      const { containerId } = await this.ensureMatterContainer(matter);
      const containerCreated = !matter.speContainerId && !!containerId;

      // Get documents from SharePoint Embedded
      const documents = await this.speService.listFiles(containerId);

      // Convert to legal documents with metadata
      const legalDocuments = documents.map(doc => ({
        ...doc,
        matterId: matter.id,
        category: 'other' as const, // Default category, TODO: get from metadata
        confidentialityLevel: 'internal' as const, // Default level, TODO: get from metadata
        tags: [], // TODO: get from metadata
        version: 1 // TODO: get from metadata
      }));

      return { 
        documents: legalDocuments, 
        containerCreated 
      };
    } catch (error) {
      console.error("Error getting matter documents:", error);
      
      // Check if this is our user-friendly setup message
      if (error instanceof Error && error.message.startsWith("SETUP_REQUIRED:")) {
        // Re-throw as-is to preserve the user-friendly message
        throw error;
      }
      
      throw new Error(`Failed to get documents for matter: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Downloads a document from a matter
   * @param documentId Document ID to download
   * @param matter Matter the document belongs to
   */
  public async downloadDocument(documentId: string, matter: Matter): Promise<{ blob: Blob; filename: string }> {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      return await this.speService.downloadFile(containerId, documentId);
    } catch (error) {
      console.error("Error downloading document:", error);
      throw new Error(`Failed to download document: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Deletes a document from a matter
   * @param documentId Document ID to delete
   * @param matter Matter the document belongs to
   */
  public async deleteDocument(documentId: string, matter: Matter): Promise<void> {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      await this.speService.deleteFile(containerId, documentId);
    } catch (error) {
      console.error("Error deleting document:", error);
      throw new Error(`Failed to delete document: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  // =====================================
  // FOLDER OPERATIONS - Phase 3 Enhancement
  // =====================================

  /**
   * Creates a new folder in a matter's container
   * @param matter Matter entity
   * @param folderName Name of the folder to create
   * @param parentPath Optional parent folder path (default: root)
   * @param config Optional configuration for folder creation
   */
  public async createFolder(
    matter: Matter,
    folderName: string,
    parentPath: string = '',
    config: { conflictBehavior: 'fail' | 'replace' | 'rename'; validateDepth?: boolean; maxDepth?: number } = { conflictBehavior: 'rename', validateDepth: true, maxDepth: 3 }
  ) {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      return await this.speService.createFolder(containerId, folderName, parentPath, config);
    } catch (error) {
      console.error("Error creating folder:", error);
      throw new Error(`Failed to create folder: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Lists all folders in a matter's container or specific path
   * @param matter Matter entity
   * @param path Optional path to list folders from (default: root)
   */
  public async listFolders(matter: Matter, path: string = '') {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      return await this.speService.listFolders(containerId, path);
    } catch (error) {
      console.error("Error listing folders:", error);
      throw new Error(`Failed to list folders: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Deletes a folder from a matter's container
   * @param matter Matter entity
   * @param folderId Folder ID to delete
   */
  public async deleteFolder(matter: Matter, folderId: string): Promise<void> {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      await this.speService.deleteFolder(containerId, folderId);
    } catch (error) {
      console.error("Error deleting folder:", error);
      throw new Error(`Failed to delete folder: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Moves a document to a different folder within the matter's container
   * @param matter Matter entity
   * @param documentId Document ID to move
   * @param targetPath Target folder path or folder ID
   * @param useIdBasedTarget Whether targetPath is a folder ID (true) or path (false)
   */
  public async moveDocument(
    matter: Matter,
    documentId: string,
    targetPath: string,
    useIdBasedTarget: boolean = false
  ) {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      return await this.speService.moveDocument(containerId, documentId, targetPath, useIdBasedTarget);
    } catch (error) {
      console.error("Error moving document:", error);
      throw new Error(`Failed to move document: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Lists documents in a specific folder within the matter's container
   * @param matter Matter entity
   * @param folderPath Folder path to list documents from
   */
  public async listDocumentsInFolder(matter: Matter, folderPath: string = ''): Promise<LegalDocument[]> {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      const documents = await this.speService.listDocumentsInFolder(containerId, folderPath);
      
      // Transform SPEDocuments to LegalDocuments
      return documents.map(doc => ({
        ...doc,
        matterId: matter.id,
        category: 'other' as const,
        confidentialityLevel: 'internal' as const
      }));
    } catch (error) {
      console.error("Error listing documents in folder:", error);
      throw new Error(`Failed to list documents in folder: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Gets folder information by path or ID
   * @param matter Matter entity
   * @param folderIdentifier Folder path or ID
   * @param useIdBased Whether to use ID-based lookup (true) or path-based (false)
   */
  public async getFolderInfo(
    matter: Matter,
    folderIdentifier: string,
    useIdBased: boolean = false
  ) {
    try {
      const { containerId } = await this.ensureMatterContainer(matter);
      return await this.speService.getFolderInfo(containerId, folderIdentifier, useIdBased);
    } catch (error) {
      console.error("Error getting folder info:", error);
      throw new Error(`Failed to get folder info: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Comprehensive diagnostic method for debugging container issues
   * Returns detailed information about a matter's container state
   */
  public async diagnoseMatterContainers(matter: Matter): Promise<{
    matterInfo: {
      id: string;
      title: string;
      speContainerIdInDB: string | undefined;
    };
    databaseState: {
      hasContainerIdSaved: boolean;
      savedContainerId: string | null;
    };
    sharePointState: {
      containersFound: Array<{
        id: string;
        displayName: string;
        description: string;
        createdDate: Date;
      }>;
      totalContainersForMatter: number;
    };
    recommendations: string[];
  }> {
    const timestamp = new Date().toISOString();
    console.info(`🔍 [${timestamp}] Starting container diagnosis for matter ${matter.id}`);

    const diagnosis = {
      matterInfo: {
        id: matter.id,
        title: matter.title,
        speContainerIdInDB: matter.speContainerId
      },
      databaseState: {
        hasContainerIdSaved: !!matter.speContainerId,
        savedContainerId: matter.speContainerId || null
      },
      sharePointState: {
        containersFound: [] as Array<{
          id: string;
          displayName: string;
          description: string;
          createdDate: Date;
        }>,
        totalContainersForMatter: 0
      },
      recommendations: [] as string[]
    };

    try {
      // Get all matter containers from SharePoint
      const allContainers = await this.speService.getMatterContainers();
      
      // Filter containers that match this matter
      const matterContainers = allContainers.filter(container => 
        container.displayName.includes(matter.id) || 
        container.description?.includes(matter.id) ||
        container.displayName.includes(matter.title.replace(/\s+/g, '_'))
      );

      diagnosis.sharePointState.containersFound = matterContainers.map(c => ({
        id: c.id,
        displayName: c.displayName,
        description: c.description || '',
        createdDate: new Date(c.createdDateTime)
      }));
      
      diagnosis.sharePointState.totalContainersForMatter = matterContainers.length;

      // Generate recommendations based on findings
      if (diagnosis.sharePointState.totalContainersForMatter === 0) {
        diagnosis.recommendations.push("✅ No containers found - this is normal for a new matter");
      } else if (diagnosis.sharePointState.totalContainersForMatter === 1) {
        if (diagnosis.databaseState.hasContainerIdSaved) {
          diagnosis.recommendations.push("✅ Perfect state: 1 container exists and is saved in database");
        } else {
          diagnosis.recommendations.push("⚠️ 1 container exists but not saved to database - will be fixed on next operation");
        }
      } else {
        diagnosis.recommendations.push(`🚨 DUPLICATE ISSUE: ${diagnosis.sharePointState.totalContainersForMatter} containers found for this matter!`);
        diagnosis.recommendations.push("🔧 Use the cleanup function to remove duplicates");
        
        if (!diagnosis.databaseState.hasContainerIdSaved) {
          diagnosis.recommendations.push("🔧 Database persistence missing - contributing to duplicate creation");
        }
      }

      // Check if saved container ID actually exists
      if (diagnosis.databaseState.hasContainerIdSaved) {
        const savedContainerExists = matterContainers.some(c => c.id === diagnosis.databaseState.savedContainerId);
        if (!savedContainerExists) {
          diagnosis.recommendations.push("🚨 Database contains invalid container ID - container was deleted!");
          diagnosis.recommendations.push("🔧 Clear the speContainerId from database or run ensureMatterContainer to fix");
        }
      }

      console.info(`🔍 [${timestamp}] Diagnosis complete for matter ${matter.id}:`, diagnosis);
      return diagnosis;

    } catch (error) {
      console.error(`💥 [${timestamp}] Diagnosis failed for matter ${matter.id}:`, error);
      diagnosis.recommendations.push(`❌ Diagnosis failed: ${error instanceof Error ? error.message : String(error)}`);
      return diagnosis;
    }
  }

  /**
   * Debug method to get current container operations status
   * Useful for diagnosing race conditions
   */
  public static getContainerOperationsStatus(): { 
    ongoingOperations: string[]; 
    operationCount: number; 
  } {
    const ongoingOperations = Array.from(LegalDocumentService.containerOperations.keys());
    return {
      ongoingOperations,
      operationCount: ongoingOperations.length
    };
  }

  /**
   * Gets all containers for legal matters
   * @returns Array of SPE containers for matters
   */
  public async getAllMatterContainers(): Promise<SPEContainer[]> {
    try {
      return await this.speService.getMatterContainers();
    } catch (error) {
      console.error("Error getting matter containers:", error);
      throw new Error(`Failed to get matter containers: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Validates file before upload
   * @param file File to validate
   */
  private validateFile(file: File): void {
    // Maximum file size: 100MB (SharePoint Embedded limit)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    
    if (file.size > maxSize) {
      throw new Error(`File size (${Math.round(file.size / 1024 / 1024)}MB) exceeds maximum limit of 100MB`);
    }

    // Check for potentially dangerous file types
    const dangerousExtensions = ['.exe', '.bat', '.cmd', '.com', '.scr', '.vbs', '.js'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (dangerousExtensions.includes(fileExtension)) {
      throw new Error(`File type ${fileExtension} is not allowed for security reasons`);
    }
  }
}

export default LegalDocumentService;

// Export a singleton instance to prevent race conditions from multiple service instances
export const legalDocumentService = new LegalDocumentService();
